import { calculateAgeForTest, displayPatientName, formatLongDate } from "../../../utils/CommonUtil";
import { useEffect, useState } from "react";

import { OcularMotilityMetrics, TestType } from "../../../types/testTypes";
import { useSearchParams } from "react-router-dom";
import useTestList from "../../../hooks/useTestList";

export interface PatientData {
  name: string;
  age: number;
  testDate: string;
}

export interface PupillometryData {
  status: string;
  chartData?: any[];
  testType?: TestType | null;
}

export interface OcularMotilityData {
  status: string;
  metrics: OcularMotilityMetrics;
  chartUrl?: string;
  chartData?: any[];
  testType?: TestType | null;
}

export interface NeurologicalDataParams {
  pupillometryUuid?: string;
  ocularMotilityUuid?: string;
}

export default function useNeurologicalData({
  pupillometryUuid,
  ocularMotilityUuid,
}: NeurologicalDataParams = {}) {
  const [searchParams] = useSearchParams();

  const pupillometryUuid_ = pupillometryUuid ?? searchParams.get("pupillometryUuid");
  const ocularMotilityUuid_ = ocularMotilityUuid ?? searchParams.get("ocularMotilityUuid");

  const { testList: pupillometryTestList, testListLoading: pupillometryTestListLoading } =
    useTestList(pupillometryUuid_ ? { uuid: pupillometryUuid_ } : {});
  const { testList: ocularMotilityTestList, testListLoading: ocularMotilityTestListLoading } =
    useTestList(ocularMotilityUuid_ ? { uuid: ocularMotilityUuid_ } : {});

  const [patientData, setPatientData] = useState<PatientData>({
    name: "Emma Thompson",
    age: 8,
    testDate: "November 20, 2025",
  });

  const [pupillometryData, setPupillometryData] = useState<PupillometryData>({
    status: "unknown",
    chartData: [],
    testType: undefined,
  });

  const [ocularMotilityData, setOcularMotilityData] = useState<OcularMotilityData>({
    status: "unknown",
    metrics: {
      targetVelocity: 4,
      rightMeanError: 1.061,
      rightMeanTangentialVelocity: 2.463,
      rightPursuitGain: 0.616,
      rightSaccadeCount: 0,
      leftMeanError: 1.297,
      leftMeanTangentialVelocity: 2.443,
      leftPursuitGain: 0.611,
      leftSaccadeCount: 0,
    },
    chartUrl: undefined,
    chartData: [],
    testType: undefined,
  });

  useEffect(() => {
    if (!pupillometryTestListLoading && pupillometryTestList) {
      let testData = null;
      if (
        !Array.isArray(pupillometryTestList) &&
        (pupillometryTestList as any)?.data &&
        Array.isArray((pupillometryTestList as any).data)
      ) {
        testData = (pupillometryTestList as any).data[0];
      } else if (Array.isArray(pupillometryTestList)) {
        testData = pupillometryTestList[0];
      } else {
        testData = pupillometryTestList;
      }

      if (testData) {
        const patient = testData.patient;
        if (patient) {
          const patientAge = patient.dob
            ? calculateAgeForTest(new Date(testData.startDate), new Date(patient.dob))
            : 0;

          setPatientData({
            name: patient.name ? displayPatientName(patient.name) : "Unknown",
            age: patientAge,
            testDate: formatLongDate(testData.startDate),
          });
        }

        if (testData.results && Array.isArray(testData.results) && testData.results.length > 0) {
          const processedResults = testData.results.map((point: any) => ({
            ...point,
            targetEye: point.targetEye || "Both",
          }));

          setPupillometryData({
            status: "unknown",
            chartData: processedResults,
            testType: testData.type || testData.testType,
          });
        }
      }
    }
  }, [pupillometryTestList, pupillometryTestListLoading]);

  useEffect(() => {
    if (!ocularMotilityTestListLoading && ocularMotilityTestList) {
      let testData = null;
      if (
        !Array.isArray(ocularMotilityTestList) &&
        (ocularMotilityTestList as any)?.data &&
        Array.isArray((ocularMotilityTestList as any).data)
      ) {
        testData = (ocularMotilityTestList as any).data[0];
      } else if (Array.isArray(ocularMotilityTestList)) {
        testData = ocularMotilityTestList[0];
      } else {
        testData = ocularMotilityTestList;
      }

      if (testData) {
        if (!pupillometryUuid) {
          const patient = testData.patient;
          if (patient) {
            const patientAge = patient.dob
              ? calculateAgeForTest(new Date(testData.startDate), new Date(patient.dob))
              : 0;

            setPatientData({
              name: patient.name ? displayPatientName(patient.name) : "Unknown",
              age: patientAge,
              testDate: formatLongDate(testData.startDate),
            });
          }
        }

        if (testData.results?.timestamps) {
          const res = testData.results;
          const chartData = [];
          res.timestamps.forEach((timestamp, i) => {
            chartData.push({
              rightGazePosition: res?.rightGazePositions?.[i] ?? null,
              leftGazePosition: res?.leftGazePositions?.[i] ?? null,
              targetPosition: res?.targetPositions?.[i] ?? null,
              rightEyeError: res?.rightEyeErrors?.[i] ?? null,
              rightEyeTangentialVelocity: res?.rightEyeTangentialVelocities?.[i] ?? null,
              leftEyeError: res?.leftEyeErrors?.[i] ?? null,
              leftEyeTangentialVelocity: res?.leftEyeTangentialVelocities?.[i] ?? null,
              timestamp: timestamp,
            });
          });

          setOcularMotilityData({
            status: "unknown",
            metrics: testData.results,
            chartUrl: undefined,
            chartData: chartData,
            testType: testData.type || testData.testType,
          });
        }
      }
    }
  }, [ocularMotilityTestList, ocularMotilityTestListLoading, pupillometryUuid]);

  const loading = pupillometryTestListLoading || ocularMotilityTestListLoading;

  return {
    patientData,
    pupillometryData,
    ocularMotilityData,
    loading,
  };
}
